import { DocumentEventsService } from 'src/app/shared/services/document-events/document-events.service';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  input,
  Input,
  InputSignal,
  NgZone,
  OnInit,
  Output,
  signal,
  TemplateRef,
  TrackByFunction,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CellClickEvent,
  CheckboxColumnComponent,
  ColumnBase,
  ColumnReorderEvent,
  CommandColumnComponent,
  EditEvent,
  GridModule,
  PageChangeEvent,
  SelectionEvent,
  SharedModule
} from '@progress/kendo-angular-grid';
import { SafePipe } from 'safe-pipe';
import { GridCellComponent } from './cells/grid-cell/grid-cell.component';
import { SkeletonModule } from '@progress/kendo-angular-indicators';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { GridColumnType } from '../../../../models/ts/grid-column-type.model';
import { CellActionData } from '../../../features/bizzmine/widgets/collection-list-widget/interfaces/cell-action-data';
import { CellActionType } from '../../../features/bizzmine/widgets/collection-list-widget/classes/cell-action-type';
import { PagerModule } from '@progress/kendo-angular-pager';
import { GroupDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import { GridCellActionsComponent } from './cells/grid-cell-actions/grid-cell-actions.component';
import { LoaderComponent } from '../ui/loader/loader.component';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';
import { IconComponent } from '../ui/icon/icon.component';
import { GridOptions } from '../../classes/list/grid-options';
import { GridContextPermissionsMetadata } from '../../../features/bizzmine/widgets/collection-list-widget/interfaces/grid-context-permissions-metadata';
import * as Sentry from '@sentry/angular';
import { GridMobileCellComponent } from './cells/grid-mobile-cell/grid-mobile-cell.component';
import { GridColumnBase } from '../../classes/list/grid-column-base';
import { FormGroup } from '@angular/forms';
import { RowArgs } from '@progress/kendo-angular-grid/rendering/common/row-args';
import { asyncScheduler, BehaviorSubject, debounceTime, distinctUntilChanged, map, take } from 'rxjs';
import { AlignmentType } from 'src/models/ts/alignment-type.model';
import { resizeLastGridColumn } from './helpers/resize-last-grid-column';
import { TableFieldDataType } from 'src/models/ts/table-field-data-type.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DragTargetContainerDirective,
  DragTargetDragEndEvent,
  DropTargetContainerDirective,
  DropTargetEvent
} from '@progress/kendo-angular-utils';
import { GridGroupHeaderNumericComponent } from './group-headers/grid-group-header-numeric/grid-group-header-numeric.component';
import { GridGroupHeaderExtensionComponent } from './group-headers/grid-group-header-extension/grid-group-header-extension.component';
import { GridComponent as KendoGridComponent } from '@progress/kendo-angular-grid/grid.component';
import { ChangeDetectionService } from 'src/app/core/services/change-detection/change-detection.service';
import { ProtectedFieldType } from 'src/models/ts/protected-field-type.model';
import { formsActions } from 'src/app/store/features/forms/forms-actions';
import { PwaService } from 'src/app/core/services/pwa/pwa.service';
import { NetworkService } from 'src/app/core/services/network/network.service';

const DROP_TARGET_ROW_FILTER = 'k-table-row';
const DROP_TARGET_GROUP_ROW_FILTER = 'k-grouping-row';


@Component({
  selector: 'bizz-grid',
  standalone: true,
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'class': 'bizz-grid' },
  imports: [
    CommonModule,
    GridModule,
    PagerModule,
    SafePipe,
    SharedModule,
    SkeletonModule,
    TranslatePipe,
    NgScrollbarModule,
    IconComponent,
    PagerModule,
    LoaderComponent,
    GridMobileCellComponent,
    GridCellActionsComponent,
    GridCellComponent,
    DragTargetContainerDirective,
    DropTargetContainerDirective,
    GridCellComponent,
    GridGroupHeaderNumericComponent,
    GridGroupHeaderExtensionComponent
  ]
})
@Sentry.TraceClass({ name: 'GridComponent' })
export class GridComponent implements OnInit, AfterViewInit {

  @ViewChild('wrapper', { read: ViewContainerRef })
  public wrapperVCR: ViewContainerRef;

  @ViewChild('wrapper', { read: DragTargetContainerDirective })
  public dragTargetContainer: DragTargetContainerDirective;

  @ViewChild('wrapper', { read: DropTargetContainerDirective })
  public dropTargetContainer: DropTargetContainerDirective;
  /**
   * Emits when the page changes.
   */
  @Output() public pageChange = new EventEmitter<PageChangeEvent>();
  /**
   * Emits when a cell action is triggered.
   * Currently unimplemented.
   */
  @Output() public cellAction = new EventEmitter<{ action: CellActionType, data: CellActionData }>();
  /**
   * Emits when the group or group order changes.
   */
  @Output() public groupChange = new EventEmitter<GroupDescriptor[]>();
  /**
   * Emits when the sort order changes.
   */
  @Output() public sortChange = new EventEmitter<SortDescriptor[]>();
  /**
   * Emits when selection is enabled in GridOptions and a row selection is made.
   */
  @Output() public selectionChange = new EventEmitter<any[]>();
  /**
   * Emits when a row is dragged and dropped (reordered).
   */
  @Output() public onRowDrop = new EventEmitter<DropTargetEvent>();
  /**
   * Emits when a row is dragged and dropped on a group row.
   */
  @Output() public onGroupRowDrop = new EventEmitter<DropTargetEvent>();
  @Output() public onRowDragEnter = new EventEmitter<DropTargetEvent>();
  /**
   * Optional custom column template that can be used for the grid
   * This is to overwrite the columns used by gridOptions.columns
   * use let-data: { data:any, col: GridColumnBase } to access column and data in the template.
   */
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public columnTemplate: TemplateRef<{ $implicit: any; column: GridColumnBase; columnIndex: number; }> | null;
  @Input() public columnHeaderTemplate: TemplateRef<{ column: GridColumnBase; columnIndex: number; }> | null;
  /**
   * Optional custom commmand column template that can be used to set the columns for the grid.
   * use let-dataItem let-rowIndex="rowIndex" let-column="column" to access grid data in the template.
   */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public commandColumnTemplate: TemplateRef<{
    $implicit: any;
    rowIndex: number;
    column: GridColumnBase;
  }> | null = null;
  /**
   * Optional edit column template that can be used to set the edit (input) mode of columns.
   * use let-dataItem let-formGroup="formGroup" let-column="column" to access grid data in the template.
   */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public editColumnTemplate: TemplateRef<{
    $implicit: any;
    formGroup: FormGroup;
    column: GridColumnBase;
    columnIndex: number;
  }> | null;
  /**
   * The grid options.
   * Can be created from GridOptionsDto
   * Holds information about the column options, pagesize, skip, etc.
   * @required
   **/
  private _gridOptions: GridOptions;

  @Input({ required: true })
  public get gridOptions(): GridOptions {
    return this._gridOptions;
  }

  public set gridOptions(value: GridOptions) {
    this._gridOptions = value;
    ChangeDetectionService.detectChanges(this.cdr);
  }

  /**
   * The total number of items in the grid.
   * @required
   */
  @Input({ required: true }) public totalItems: number;
  /**
   * The permissions needed for the action cell.
   * If your grid has an action cell, you need to provide this.
   */
  @Input({ alias: 'gridCellMetadata' }) public actionCellData: GridContextPermissionsMetadata;
  /**
   * Override to set the grid in readonly mode (when used in a form)
   * @type {boolean}
   */
  @Input() public readOnly = false;
  /**
   * Page sizes to show in the pager.
   * Defaults to 5, 10, 20, 50, 100 and 200
   */
  @Input() public pageSizes = [5, 10, 20, 50, 100, 200];
  /**
   * Show the pager of the grid
   * Defaults to true
   */
  private _showPager: boolean = true;

  @Input()
  public get showPager(): boolean {
    return this._showPager;
  }

  public set showPager(value: boolean) {
    this._showPager = value;
    ChangeDetectionService.detectChanges(this.cdr);
  }

  /**
   * Show the columns in mobile card view or not.
   * Defaults to false (should be true for forms)
   */
  @Input() public showMobileColumnView: boolean = false;
  /**
   * Fit columns to content. This is a configurable option for list widgets.
   * The last column will always auto size to the remaining space.
   */
  @Input() public autoFitColumns: boolean = false;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public selection: any[] = [];
  /**
   * Whether the grid rows are reorderable or not.
   */
  public rowReorderable: InputSignal<boolean> = input(false);
  public updateColumnsOnTogglingRowReodering = effect(() => {
    this.rowReorderable(); //for effect to trigger, put input here.
    if (this.autoFitColumns)
      this.updateColumnWidth();
  });
  public cleanupMoveCursorsEffect = effect(() => {
    this.rowReorderable();
    this.updateMouseCursor('');
  });
  public gridDataInternal: any[] = [];
  public currentPageInternal: number = 1;
  public readonly gridColumnType = GridColumnType;
  //The minimum width of a column when resizing in px (fallback value, see gridOptions class).
  public minResizableWidth = 50;
  public totalGridWidth: number;
  public initialLastColumnWidth?: number = undefined;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public readonly MAX_MOBILE_WIDTH = 1024;
  public AlignmentType = AlignmentType;
  public gridWidth$ = new BehaviorSubject<number>(0);
  public TableFieldDataType = TableFieldDataType;
  public injector = inject(Injector);
  @ViewChild('kendoGrid')
  private kendoGridComponent: KendoGridComponent;
  @ViewChild('kendoGrid', { read: ElementRef })
  private kendoGridElement: ElementRef;
  /**
   * The loading state of the grid.
   * Default is true.
   */
  private _loading = true;
  private intersectionObserver: IntersectionObserver;
  private resizeObserver: ResizeObserver;
  private lastObservedElementWidth: number = 0;
  private isInView = signal(false);
  private ngZone: NgZone = inject(NgZone);
  private destroyRef = inject(DestroyRef);
  private documentEventsService: DocumentEventsService = inject(DocumentEventsService);
  private gridResizeDebounceTime = 100;
  private hiddenGroupedColumns: GridColumnBase[] = [];

  private pwaService =inject(PwaService);
  private networkService = inject(NetworkService);
  private cdr = inject(ChangeDetectorRef);

  public rowClass = (args : any) => (
    {
    "k-disabled": (this.pwaService.isPwa() && !this.networkService.isOnline && !args.dataItem.isAvailableOffline)
  });

  public constructor() {
    ChangeDetectionService.detach(this.cdr);
  }

  @Input({ required: true })
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(value: boolean) {
    this._loading = value ?? false;

    if (this._loading != null) {
      ChangeDetectionService.detectChanges(this.cdr);
    }
  }

  /**
   * The data to show in the grid.
   */
  @Input()
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  public set gridData(value: any[]) { //Kendo takes any[]...
    this.gridDataInternal = value ?? [];
    this.updateColumnWidth();
    if (this.dragTargetContainer && this.dropTargetContainer) {
      this.dragTargetContainer.notify();
      this.dropTargetContainer.notify();
    }
    asyncScheduler.schedule(() => {
      ChangeDetectionService.detectChanges(this.cdr);
    });
  }

  /**
   * The current page of the grid.
   * Default is 1.
   * @required
   */
  @Input({ required: true })
  public set currentPage(value: number) {
    if (value > 0)
      this.currentPageInternal = value;
    else throw new Error('Current page must be greater than 0');
  }

  private get kendoGridColumns(): ColumnBase[] {
    return this.kendoGridComponent.columns.filter(f => !(f instanceof CommandColumnComponent)
      && !(f instanceof CheckboxColumnComponent) && !f.hidden);
  }

  //Needed to suppress constant warnings: Grid column field name 'Fields[0].Value' does not look like a valid JavaScript identifier.

  @Input() public trackBy: TrackByFunction<any> = (index: number, row: any) => {
    return index;
  };

  public ngOnInit(): void {
    this.pageSizes = (this.gridOptions.pageable.pageSizes as number[]) ?? this.pageSizes;
    this.gridResizeDebounceTime = this.autoFitColumns ? 100 : 5;
    this.hideGroupedColumns(this.gridOptions.dataSource.group);

    //Resize the action column on mobile
    this.documentEventsService.isMobile.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isMobile) => {
        const actionCol = this.gridOptions.columns.find(c => this.gridOptions.isActionsColumn(c.GridColumnType));
        if (actionCol) {
          actionCol.autoSize = !isMobile;
          actionCol.minResizableWidth = isMobile ? this.gridOptions.minResizeWidthMobileColumn : this.gridOptions.defaultWidthActionsColumn;
          actionCol.width = isMobile ? this.gridOptions.minResizeWidthMobileColumn : this.gridOptions.defaultWidthActionsColumn;
          ChangeDetectionService.detectChanges(this.cdr);
        }
      });
  }

  @Sentry.TraceMethod({ name: 'ngOnDestroy' })
  public ngOnDestroy(): void {
    this.kendoGridComponent?.columnResize?.unsubscribe();
    this.gridWidth$?.unsubscribe();
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  //Workaround so the console warning about invalid field names is not shown.
  // Fields[0].FieldName is a valid field name, but kendo won't allow it. (it's needed for form list grouping to work).
  public hideKendoGridFieldsWarning(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (this.kendoGridComponent) {
      (this.kendoGridComponent as any).verifySettings = (): boolean => {
        return true;
      };
    }
  }

  /**
   * Force detect changes to update the grid when needed.
   */
  public forceDetectChanges(): void {
    ChangeDetectionService.detectChanges(this.cdr);
  }

  public detailCollapsed(): void {
    asyncScheduler.schedule(() => {
      ChangeDetectionService.detectChanges(this.cdr);
    });
  }

  public ngAfterViewInit(): void {
    if (this.autoFitColumns) {
      this.kendoGridComponent.columnResize
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.resizeLastGridColumn();
        });
      ChangeDetectionService.detectChanges(this.cdr);
    }

    //Intersection observer to check if the grid is in view
    //This is needed to reliably autofit columns, because autofitting columns when the grid is not in view
    //will cause the columns to be resized to 0.
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      if ((entry.isIntersecting &&
          this.lastObservedElementWidth != entry.boundingClientRect.width) ||
        entry.isIntersecting && this.isInView() == false) {
        this.lastObservedElementWidth = entry.boundingClientRect.width;
        this.gridWidth$.next(this.lastObservedElementWidth);
      }
      this.isInView.set(entry.isIntersecting);
    });

    //Resize observer to update the width of the columns when the window or grid is resized.
    this.resizeObserver = new ResizeObserver(([entry]) => {
      this.ngZone.run(() => {
        if (this.lastObservedElementWidth != entry.contentRect.width) {
          this.lastObservedElementWidth = entry.contentRect.width;
          this.gridWidth$.next(this.lastObservedElementWidth);
        }
      });
    });

    this.intersectionObserver.observe(this.kendoGridElement.nativeElement);
    this.resizeObserver.observe(this.kendoGridElement.nativeElement);
    this.gridWidth$.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged(),
      debounceTime(this.gridResizeDebounceTime),
      map(() => this.updateColumnWidth()))
      .subscribe();
  }

  @ViewChild('kendoGrid', { read: ViewContainerRef }) kendoGridVCR!: ViewContainerRef;

  public onGroupChange(event: GroupDescriptor[]): void {
    // Default dir is undefined, which will causes API error (expects 'asc' or 'desc')
    event.forEach(group => {
      if (group.dir == undefined) {
        group.dir = 'asc';
      }
    });
    this.gridOptions.dataSource.group = event;

    this.hideGroupedColumns(event);

    this.groupChange.emit(event);
    this.dragTargetContainer.notify();
    this.dropTargetContainer.notify();
    ChangeDetectionService.detectChanges(this.cdr);
  }

  public onSortChange(sort: SortDescriptor[]): void {
    //ignore sort without direction
    const newSort = sort.filter(s => s.dir != undefined);
    this.gridOptions.dataSource.sort = newSort;
    this.sortChange.emit(newSort);
    ChangeDetectionService.detectChanges(this.cdr);
  }

  public onPageChange(event: PageChangeEvent): void {
    this.kendoGridComponent.scrollTo({ row: 0 });
    this.pageChange.emit(event);
    ChangeDetectionService.detectChanges(this.cdr);
  }

  public onEditRow(event: EditEvent): void {
  }

  public dragData = ({ dragTarget }: { dragTarget: HTMLElement }): number => {
    return Number(dragTarget.getAttribute('data-kendo-grid-item-index'));
  };

  public handleDragEnter(e: DropTargetEvent): void {
    this.onRowDragEnter.emit(e);
    ChangeDetectionService.detectChanges(this.cdr);
  }

  public handleRowDragEnd(e: DragTargetDragEndEvent): void {
    //Drop Events don't always detect the group row, so we need to manually handle the dragend event and detect the dropped element.
    const dropEl = document.elementFromPoint(e.dragEvent.clientX, e.dragEvent.clientY)?.closest('.' + DROP_TARGET_ROW_FILTER) as HTMLElement;
    if (dropEl) {
      const dropEvent = new DropTargetEvent();
      dropEvent.dropTarget = dropEl;
      dropEvent.dragData = this.dragData(e);
      dropEvent.dropTargetIndex = this.dragData({ dragTarget: dropEl });
      dropEvent.dragEvent = e.dragEvent;
      if (dropEl.classList.contains(DROP_TARGET_GROUP_ROW_FILTER)) {
        this.onGroupRowDrop.emit(dropEvent);
      } else
        this.onRowDrop.emit(dropEvent);

    }
    this.dragTargetContainer.notify();
    this.dropTargetContainer.notify();
    ChangeDetectionService.detectChanges(this.cdr);
  }

  /**
   * Update the elements in the grid to show a cursor when dragging.
   * The master rows, table cells and the row hint will get the cursor.
   * @param {string} cursor The cursor to show can be empty to remove the cursor.
   */
  public updateMouseCursor(cursor: string): void {
    if (cursor)
      this.kendoGridElement.nativeElement.style.setProperty('cursor', cursor, 'important');
    else this.kendoGridElement.nativeElement.style.removeProperty('cursor');
    this.kendoGridElement.nativeElement.querySelectorAll('.k-master-row').forEach((el: HTMLElement) => {
      if (cursor) {
        el.style.setProperty('cursor', cursor, 'important');
      } else el.style.removeProperty('cursor');
    });
    this.kendoGridElement.nativeElement.querySelectorAll('.k-table-td').forEach((el: HTMLElement) => {
      if (cursor) {
        el.style.setProperty('cursor', cursor, 'important');
      } else el.style.removeProperty('cursor');
    });
    const rowHint: HTMLElement | null = document.body.querySelector('.grid-move-row-hint');
    if (rowHint)
      rowHint.style.setProperty('cursor', cursor, 'important');
    ChangeDetectionService.detectChanges(this.cdr);
  }

  /**
   * Overwrites kendo's selection logic to check if a row should be selected.
   * @param {RowArgs} e
   */
  public isRowSelected(e: RowArgs): boolean {
    return this.selection.some((s) => JSON.stringify(s) === JSON.stringify(e.dataItem));
  }

  public onSelectionChange(e: SelectionEvent): void {
    if (!this.gridOptions.selectable)
      return;
    e.selectedRows?.forEach(
      (r) => this.handleSelection(r.dataItem)
    );

    e.deselectedRows?.forEach(
      (r) => this.selection = this.selection.filter((s) => s !== r.dataItem)
    );
    this.selectionChange.emit(this.selection);
    ChangeDetectionService.detectChanges(this.cdr);
  }

  public onCellClick(e: CellClickEvent): void {
    e.originalEvent.preventDefault();
    if (!this.gridOptions.selectable)
      return;
    this.handleSelection(e.dataItem);
    this.selectionChange.emit(this.selection);
    ChangeDetectionService.detectChanges(this.cdr);
  }

  public onColumnReorder(e: ColumnReorderEvent): void {
    //Don't allow reordering of other columns to the place of a non reorderable column like the actions/checkbox/status column.
    const oldCol = this.kendoGridComponent.columns.get(e.oldIndex);
    const col = this.kendoGridComponent.columns.get(e.newIndex);
    if (col && !col.reorderable || e.newIndex == undefined || e.newIndex < 0 || oldCol instanceof CommandColumnComponent) {
      e.preventDefault();
    }
    asyncScheduler.schedule(() => {
      ChangeDetectionService.detectChanges(this.cdr);
    });
  }

  /**
   * Update the column width based on is it a mobile view,
   * and resize last column to take up the remaining space
   * @private
   */
  private updateColumnWidth(): void {
    //See example kendo:
    //Currently the best way to reliably resize the columns without visible layout shift.
    //https://www.telerik.com/kendo-angular-ui/components/grid/columns/resizing/#toc-auto-fitting-the-content/
    this.ngZone.onStable
      .asObservable()
      .pipe(take(1))
      .subscribe(() => {
        if (this.autoFitColumns && this.isInView()) {
          this.fitColumnsToContent();
        }
        this.resizeLastGridColumn();
      });
  }

  /**
   * Resize the last grid column to take the remaining width of the grid and leave no
   * whitespace.
   * @private
   */
  private resizeLastGridColumn(): void {
    if (this.kendoGridElement == undefined) {
      throw new Error('kendoGridElement is undefined');
    }

    //Check if offset is needed for scrollbar
    const gcEl = this.kendoGridElement.nativeElement.querySelector('.k-grid-content');
    if (gcEl) {
      this.totalGridWidth = gcEl.offsetWidth - (gcEl.clientHeight < gcEl.scrollHeight ? 17 : 0);
      resizeLastGridColumn(this.kendoGridColumns,
        this.totalGridWidth,
        this.gridOptions.dataSource.group.length,
        this.initialLastColumnWidth);
      ChangeDetectionService.detectChanges(this.cdr);
    }
  }

  /**
   * Execute kendos autoFitColumns function and resize the status column to the minimum  mobile width.
   * Resize the last column to take up the remaining space.
   */
  private fitColumnsToContent(): void {
    if (!this.autoFitColumns) {
      console.warn('fitColumnsToContent but not autofit or no internal data');
      return;
    }

    //non resizable columns (like actions, status) need to be resizable for autofit.
    const nonResizableColumns = this.kendoGridComponent.columns.filter((c) => !c.resizable || c instanceof CommandColumnComponent);
    nonResizableColumns.forEach(c => {
      c.resizable = true;
    });
    this.kendoGridComponent.autoFitColumns();
    this.kendoGridColumns.forEach((column) => {
      if (column.width < column.minResizableWidth)
        column.width = column.minResizableWidth;
    });

    nonResizableColumns.forEach(c => {
      c.resizable = false;
      c.maxResizableWidth = c.width; //Trick to fix resizing issue
    });
  }

  /**
   * When autowidth is true we still autofit the actions, status columns and resize the last column.
   * Also make sure the column width is at least the minResizableWidth.
   */
  private autoWidthColumns(): void {
    const nonResizableColumns = this.kendoGridColumns.filter((c) => !c.resizable || c instanceof CommandColumnComponent);
    this.kendoGridColumns.forEach((column, index) => {
      if (this.gridOptions.columns[index] == undefined) {
        console.warn('Column not found in gridOptions.columns', column, index, this.gridOptions.columns);
        return;
      }
      if (nonResizableColumns.includes(column)) {
        column.resizable = true;
        this.kendoGridComponent.autoFitColumn(column);
        column.width = Math.max(column.minResizableWidth, column.width);
        column.resizable = false;
        column.maxResizableWidth = column.width;  //Trick to fix resizing issue
      } else {
        column.width = Math.max(column.minResizableWidth, this.gridOptions.columns[index].width);
      }
    });

    //@ts-expect-error undefined width is auto width, also kendo sucks.
    this.gridOptions.columns[this.gridOptions.columns.length - 1].width = undefined;
  }

  private handleSelection(dataItem: any): void {
    if (this.gridOptions.selectable.mode === 'single') {
      this.selection = [dataItem];
      return;
    }
    const isSelected = this.selection.some((s) => JSON.stringify(s) === JSON.stringify(dataItem));
    if (isSelected) {
      this.selection = this.selection.filter((s) => JSON.stringify(s) !== JSON.stringify(dataItem));
    } else {
      this.selection.push(dataItem);
    }
  }

  private hideGroupedColumns(groups: GroupDescriptor[]): void {
    //set columns that are grouped hidden
    this.gridOptions.columns.forEach(c => {
      //only hide columns that are grouped and not already hidden.
      if (groups.some(g => g.field == c.field) && !c.hidden) {
        c.hidden = true;
        if (!this.hiddenGroupedColumns.includes(c))
          this.hiddenGroupedColumns.push(c);
      }
    });
    //Iterate hidden grouped columns and set them to visible if they are not in the current groups.
    this.hiddenGroupedColumns.forEach(c => {
      if (!groups.some(g => g.field == c.field)) {
        c.hidden = false;
      }
    });
    //Filter out columns that are not hidden anymore.
    this.hiddenGroupedColumns = this.hiddenGroupedColumns.filter((c) => c.hidden);
    ChangeDetectionService.detectChanges(this.cdr);
  }


  protected readonly ProtectedFieldType = ProtectedFieldType;
}